import { useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'

import { useQuery } from '@apollo/client'
import { Box } from '@factorialco/deprecated-design-system'
import { zodResolver } from '@hookform/resolvers/zod'
import { MileageRatesDocument } from 'generated/resources/graphql'
import { useToggle } from 'usehooks-ts'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'

import FilesPreviewer from 'modules/expenses/components/expenses/Detail/_FilesPreviewer'
import MileageForm from 'modules/expenses/components/expenses/Detail/_Form/Mileage/new'
import { getMileageFormDefaultValues } from 'modules/expenses/components/expenses/Detail/_Form/Mileage/new/form/form'
import { MileageSchema } from 'modules/expenses/components/expenses/Detail/_Form/Mileage/new/form/schema'
import Sidepanel from 'modules/expenses/components/expenses/Detail/_Sidepanel'
import { BulkActionsProvider } from 'modules/expenses/components/expenses/Detail/hooks/useBulkActions'
import { FilesPreviewerProvider } from 'modules/expenses/components/expenses/Detail/hooks/useFilesPreviewer'
import CreateExpenseFooterActions from 'modules/expenses/components/expenses/New/NewForm/CreateExpenseFooterActions'
import { useEmployeeAccess } from 'modules/expenses/hooks/useEmployeeAccess'
import { normalizeEffectiveOn } from 'modules/expenses/hooks/useExpensable'
import { CustomizationField } from 'modules/expenses/hooks/useExpensesCustomization'
import { useProjectsAssignedToEmployees } from 'modules/expenses/hooks/useProjects'
import { useBudgetOptions } from 'modules/finance/hooks/useBudgets'

export interface CreateMileageFormProps {
  onClose: () => void
  hasReportOnBehalfFeature: boolean
  canSeeEmployeeLegalEntity: boolean
  canCreateExpensesForOthers: boolean
  customizationFields: CustomizationField[]
  hasProjectsFeature: boolean
  groupCurrency?: string | null
}

const CreateMileageForm = (props: CreateMileageFormProps) => {
  const {
    onClose,
    customizationFields,
    hasProjectsFeature,
    canSeeEmployeeLegalEntity,
    canCreateExpensesForOthers,
    hasReportOnBehalfFeature,
    groupCurrency,
  } = props
  const [openSidepanel, toggleOpenSidepanel] = useToggle(true)
  const [_openFilePreviewer, toggleOpenFilePreviewer] = useToggle(true)
  const [onDropHandler, setOnDropHandler] = useState<(files: File[]) => void>(() => () => {})

  const handleClose = () => {
    toggleOpenSidepanel()
    onClose()
  }
  const handleCloseClicked = () => {
    toggleOpenFilePreviewer()
  }
  const { employee, access, company } = useCurrentSession()
  const { groupId } = useParams()

  const [formEmployeeId, setFormEmployeeId] = useState<number>()

  const employeeId = formEmployeeId ?? employee?.id

  const defaultValues = getMileageFormDefaultValues({
    employee,
    customizationFields,
    groupId: groupId ? Number(groupId) : null,
    groupCurrency,
  })

  const onEffectiveOnChange = (value: string) => {
    setFormEffectiveOn(value)
  }
  const [formEffectiveOn, setFormEffectiveOn] = useState(defaultValues.effectiveOn)

  const { budgetOptions: newBudgetOptions, loading: newLoadingBudgetOptions } = useBudgetOptions({
    effectiveOn: normalizeEffectiveOn(formEffectiveOn),
    employeeId,
  })

  const { projectWorkers: newProjectWorkers, loading: newLoadingProjectWorkers } =
    useProjectsAssignedToEmployees({
      skip: hasProjectsFeature === false,
      employeeIds: employeeId ? [employeeId] : [],
    })

  const mileageForm = useForm({
    mode: 'onChange',
    resolver: zodResolver(
      MileageSchema({
        customizationFields,
        resourceType: 'mileage',
        budgetOptions: newBudgetOptions,
        projectWorkers: newProjectWorkers,
        hasProjectsFeature,
      })
    ),
    defaultValues: {
      ...defaultValues,
    },
  })

  const { watch } = mileageForm
  const { accessId: newFormAccessId } = useEmployeeAccess(formEmployeeId)

  const currency = watch('currency')

  const { data } = useQuery(MileageRatesDocument, {
    variables: {
      companyId: company.id,
      accessId: newFormAccessId || access.id,
      currency: currency,
    },
  })

  const ratesOptions = data?.expenses?.rateConfigurationsConnection?.nodes ?? []
  const { trigger } = mileageForm
  useUnsafeEffect(() => {
    trigger()
  }, [customizationFields, trigger])

  return (
    <BulkActionsProvider>
      <Sidepanel
        open={openSidepanel}
        onClose={handleClose}
        onCloseClicked={handleCloseClicked}
        canRestartApprovalFlow={false}
      >
        <FilesPreviewerProvider initialFiles={[]}>
          <FilesPreviewer
            onClose={handleClose}
            onDrop={onDropHandler}
            showUploadButton
            isFilesFieldEnabled={customizationFields.some(
              (field) => field.id === 'mileage-files' && field.enabled
            )}
          />

          <FormProvider {...mileageForm}>
            <Box padding="s24">
              <MileageForm
                customizationFields={customizationFields}
                budgetOptions={newBudgetOptions}
                projectWorkers={newProjectWorkers}
                hasProjectsFeature={hasProjectsFeature}
                loadingProjectWorkers={newLoadingProjectWorkers}
                loadingBudgetOptions={newLoadingBudgetOptions}
                registerDropHandler={setOnDropHandler}
                onEmployeeIdChange={setFormEmployeeId}
                onEffectiveOnChange={onEffectiveOnChange}
                canEditExpensesWithPendingStatus={false}
                canSeeEmployeeLegalEntity={canSeeEmployeeLegalEntity}
                canCreateExpensesForOthers={canCreateExpensesForOthers}
                hasReportOnBehalfFeature={hasReportOnBehalfFeature}
                rates={ratesOptions}
              />
            </Box>
            <CreateExpenseFooterActions
              customizationFields={customizationFields}
              onClose={handleClose}
              groupId={groupId}
              taxAgencyOrganization={null}
              formType="mileage"
            />
          </FormProvider>
        </FilesPreviewerProvider>
      </Sidepanel>
    </BulkActionsProvider>
  )
}

export default CreateMileageForm
